'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceCardProps {
  service: {
    id: string;
    title: string;
    description: string;
    icon: string;
    image: string;
    imageAlt: string;
    category: string;
    complexity: string;
    roiTimeline: string;
    businessFunction: string;
    benefits: string[];
    implementationDuration: string;
    roiMetric: string;
    detailLink: string;
  };
  isActive: boolean;
  onHover: (id: string | null) => void;
}

const ServiceCard = ({ service, isActive, onHover }: ServiceCardProps) => {
  return (
    <div
      className={`group relative rounded-2xl overflow-hidden transition-all duration-500 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow ${
        isActive ? 'scale-105 z-10' : 'scale-100'
      }`}
      onMouseEnter={() => onHover(service.id)}
      onMouseLeave={() => onHover(null)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      <div className="glassmorphic p-6 h-full flex flex-col relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-all duration-500 shadow-orange-glow"></div>
        
        <div className="relative mb-4">
          <div className="w-full h-48 rounded-xl overflow-hidden mb-4">
            <AppImage
              src={service.image}
              alt={service.imageAlt}
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
            />
          </div>
          
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                <Icon name={service.icon as any} size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                  {service.title}
                </h3>
                <span className="text-xs font-mono text-muted-foreground">{service.category}</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {service.description}
          </p>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center space-x-2">
              <Icon name="ClockIcon" size={16} />
              <span>Implementatie</span>
            </span>
            <span className="text-foreground font-semibold">{service.implementationDuration}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center space-x-2">
              <Icon name="ChartBarIcon" size={16} />
              <span>ROI Timeline</span>
            </span>
            <span className="text-foreground font-semibold">{service.roiTimeline}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center space-x-2">
              <Icon name="SignalIcon" size={16} />
              <span>Complexiteit</span>
            </span>
            <span className="text-foreground font-semibold">{service.complexity}</span>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircleIcon" size={16} className="text-success" />
            <span className="text-sm font-semibold text-foreground">ROI Resultaat</span>
          </div>
          <div className="bg-success/10 border border-success/20 rounded-lg p-3 shadow-green-glow">
            <p className="text-success font-bold text-lg">{service.roiMetric}</p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-xs font-semibold text-muted-foreground mb-2">Belangrijkste voordelen:</p>
          <ul className="space-y-1">
            {service.benefits.slice(0, 3).map((benefit, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm text-foreground">
                <Icon name="CheckIcon" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto pt-4 border-t border-border">
          <Link
            href={service.detailLink}
            className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-lg bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow"
          >
            <span className="font-semibold">Meer informatie</span>
            <Icon name="ArrowRightIcon" size={18} className="transform group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;